import { useEffect, type ReactNode } from 'react';

/**
 * Таблица.
 *
 * На ширине до 760 строки становятся карточками — это делает CSS кита
 * по атрибуту `data-col` на ячейке. Отдельной мобильной разметки нет,
 * поэтому роли колонок обязательны:
 *
 *   primary    — главное поле строки, занимает всю ширину карточки
 *   secondary  — второе значение, встаёт справа от главного
 *   sec2       — третье значение, уходит на вторую строку карточки
 *   act        — действие строки
 *   hide       — на телефоне не показывается
 *   без роли   — уходит вниз карточки мелким текстом
 *
 * Подпись колонки для карточки пишется внутри ячейки в `.clbl`.
 */

export function Table({
  head,
  children,
  valueWidth,
}: {
  /** Шапку можно не давать: у мелкой повторяющейся таблицы она превращается в шум */
  head?: ReactNode;
  children: ReactNode;
  /** --tbl-val: ширина колонки со значением, чтобы правые края сходились */
  valueWidth?: number;
}) {
  return (
    <table
      className="tbl"
      style={valueWidth ? ({ '--tbl-val': `${valueWidth}px` } as React.CSSProperties) : undefined}
    >
      {head ? <thead>{head}</thead> : null}
      <tbody>{children}</tbody>
    </table>
  );
}

/**
 * Закреплённая шапка таблицы (кит 2.15). Шапку и текущую группу держит
 * CSS кита (`position: sticky`), коду остаются два признака:
 *
 *   .stuck — пока верх таблицы ушёл за край окна, а низ ещё виден:
 *            только тогда под шапкой тень;
 *   --th-h — фактическая высота шапки: на ней прилипает строка группы.
 *
 * Один обработчик на всё приложение, а не на таблицу: реестры — разметка
 * `table.tbl` из макетов, через компонент идёт меньшая часть. Ставится
 * в `Shell`.
 */
export function useStickyTableHeads(): void {
  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      document.querySelectorAll<HTMLTableElement>('table.tbl').forEach((t) => {
        if (t.tHead) {
          const h = `${t.tHead.getBoundingClientRect().height}px`;
          if (t.style.getPropertyValue('--th-h') !== h) t.style.setProperty('--th-h', h);
        }
        const r = t.getBoundingClientRect();
        t.classList.toggle('stuck', r.top < 0 && r.bottom > 0);
      });
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    addEventListener('scroll', schedule, { passive: true });
    addEventListener('resize', schedule);
    schedule();
    return () => {
      removeEventListener('scroll', schedule);
      removeEventListener('resize', schedule);
      cancelAnimationFrame(frame);
    };
  }, []);
}

/** Прочерк «нет данных» в ячейке (кит 2.15, `.na`): тихий, пустое не весит как число. */
export function Na() {
  return <span className="na">—</span>;
}

/** Подпись значения, видимая только в карточке на телефоне. */
export function CardLabel({ children }: { children: string }) {
  return <span className="clbl">{children}</span>;
}

/** Строка, видимая только на телефоне: под главным полем карточки. */
export function MobileSub({ children }: { children: ReactNode }) {
  return <div className="sub mob">{children}</div>;
}

/** Действие «убрать» в конце строки. */
export function RowAction({ onClick, title }: { onClick: () => void; title: string }) {
  return (
    <span className="rmv" title={title} onClick={onClick}>
      ✕
    </span>
  );
}

/**
 * Кнопки порядка — кандидат в кит (макет, `.ord`).
 *
 * Перетаскивание в таблице ненадёжно и недоступно с клавиатуры;
 * две стрелки работают везде, в том числе на телефоне.
 */
export function OrderButtons({
  onUp,
  onDown,
  canUp,
  canDown,
}: {
  onUp: () => void;
  onDown: () => void;
  canUp: boolean;
  canDown: boolean;
}) {
  return (
    <span className="ord">
      <span className={canUp ? '' : 'off'} title="Выше" onClick={canUp ? onUp : undefined}>
        ▲
      </span>
      <span className={canDown ? '' : 'off'} title="Ниже" onClick={canDown ? onDown : undefined}>
        ▼
      </span>
    </span>
  );
}

/**
 * Поле прямо в ячейке — кандидат в кит (макет, `.cellinp`).
 *
 * Обычное поле высотой 32 раздувало строку реестра вдвое. Здесь поле
 * без собственной высоты, контур появляется при наведении. Нужно там,
 * где в списке правят одно число, не открывая карточку: лимит
 * номинации, дата дедлайна.
 */
export function CellInput({
  value,
  onCommit,
  align = 'right',
}: {
  value: number | null;
  onCommit: (value: number) => void;
  align?: 'left' | 'right';
}) {
  return (
    <input
      className="cellinp"
      inputMode="numeric"
      style={align === 'left' ? { textAlign: 'left' } : undefined}
      defaultValue={value ?? ''}
      key={value ?? 'empty'}
      onBlur={(e) => {
        const next = Number(e.target.value.replace(/[^\d]/g, '')) || 0;
        if (next !== value) onCommit(next);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
    />
  );
}
